import { ref } from 'vue'
import { acceptHMRUpdate, defineStore } from 'pinia'
import { CMS, IRogue } from '@/js/services/RogueTypes'

export type CMSStoreState = {
  cms: {[key: string] : CMS},
  byIdentifier: {[key: string] : number},
  byUrl: {[key: string]: number | null}
}

export const useCmsStore = defineStore('cms', () => {
  const loadData:{[key: string]: undefined | Promise<void | CMS>} = {}

  const cms = ref<CMSStoreState['cms']>({})
  const byUrl = ref<CMSStoreState['byUrl']>({})
  const byIdentifier = ref<CMSStoreState['byIdentifier']>({})

  function setCmsData(cmsData: CMS) {
    cms.value[cmsData.id] = cmsData
    byIdentifier.value[cmsData.identifier] = cmsData.id
    let url = cmsData.url

    if (url === 'homepage_2015') {
      url = '/'
    }

    byUrl.value[url] = cmsData.id
  }

  function setIdForUrl(url: string, id: number) {
    if (!byUrl.value[url] && url !== 'homepage_2015') {
      if (url.indexOf('/') !== 0) {
        url = `/${url}`
      }
      byUrl.value[url] = id
    }
  }

  function eraseIdForUrl(url: string) {
    byUrl.value[url] = null
  }

  function load(cmsId: number | string, rogue: IRogue) {
    if (cms.value[cmsId]) {
      return Promise.resolve(cms.value[cmsId])
    }

    if (!loadData[cmsId]) {
      loadData[cmsId] = rogue.fetch<CMS>(`/svc_content/cms/page/${cmsId}`) // TODO confirm type of response. Might need a name field
        .then((data) => {
          if (data.roguebuilder?.data) {
            data.roguebuilder = data.roguebuilder.data
          }

          // We dont need Vue to turn roguebuilder into observable attribute, since it wont change
          // https://github.com/vuejs/vue/issues/2637#issuecomment-361633222
          if (Object.defineProperty) {
            Object.defineProperty(data, 'roguebuilder', { configurable: false })
          }

          setCmsData(data)
          loadData[cmsId] = undefined

          return data
        })
        .catch(e => {
          rogue.log.error(`Error loading cms ${cmsId} for ${rogue.site.code} url ${rogue.router?.currentRoute?.value.fullPath || ''}`, e?.response?.data || e?.message)
          loadData[cmsId] = undefined
        })
    }

    return Promise.resolve(loadData[cmsId])
  }

  function forceReload(cmsId: number | string) {
    // Remove the entry so the component calls the load action
    delete cms.value[cmsId]
  }

  return {
    cms,
    byUrl,
    byIdentifier,
    setIdForUrl,
    eraseIdForUrl,
    load,
    forceReload,
  }
})

if (import.meta.hot) {
  import.meta.hot.accept(acceptHMRUpdate(useCmsStore, import.meta.hot))
}